// ReplaceFileDialog — replace a document with a new version (ADR-068: the
// older version stays a file of its own, listed under Other versions). One
// act: pick the file, submit. Modal + form shell; the File itself lives beside
// the form (a File is not a form value — the upload dialog's rule), and every
// open starts with none picked. A new version is a create beside the file — no
// stamp rides it (the server judges the file still the current version).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Modal } from '../ui/Modal.js';
import { FormBody, FormFooter } from './forms/FormShell.js';
import { useFormShell } from './forms/useFormShell.js';
import { FileUpload } from '../ui/FileUpload.js';
import { useResetOnOpen } from '../../hooks/useResetOnOpen.js';
import { displayName, type LibraryFile } from './filesDisplay.js';

/** The coarse family of a picked file, from its MIME type — the kind a person recognises. */
function kindOf(type: string): 'image' | 'video' | 'audio' | 'pdf' | 'document' {
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'application/pdf') return 'pdf';
  return 'document';
}

const Schema = z.object({ picked: z.literal(true) });
type Values = z.infer<typeof Schema>;

export interface ReplaceFileDialogProps {
  open: boolean;
  file: LibraryFile;
  onClose: () => void;
  accept?: string;
  onReplace: (file: LibraryFile, next: File) => Promise<void> | void;
}

export function ReplaceFileDialog({ open, file, onClose, accept, onReplace }: ReplaceFileDialogProps) {
  const { t } = useTranslation('files');
  const [next, setNext] = useState<File | null>(null);
  useResetOnOpen(open, (isOpen) => {
    if (isOpen) setNext(null);
  });
  const shell = useFormShell<Values>({
    schema: Schema as unknown as z.ZodType<Values, Values>,
    values: () => ({ picked: true }),
    onSubmit: async () => {
      if (next) await onReplace(file, next);
    },
    onSuccess: onClose,
  });
  // Every open starts without the last attempt's line (a passive effect — an RHF reset).
  const { reseed } = shell;
  useEffect(() => {
    if (open) reseed();
  }, [open, reseed]);
  return (
    <>
      <Modal open={open} onClose={() => (next ? shell.requestLeave(onClose) : onClose())} title={t('replace.title')} description={displayName(file)} maxWidth="max-w-md" footer={<FormFooter shell={shell} onCancel={onClose} submitLabel={t('replace.submit')} submitDisabled={next === null} />}>
        <FormBody shell={shell}>
          <FileUpload file={next} onFileChange={setNext} accept={accept} label={t('replace.drop')} hint={t('replace.hint')} removeLabel={t('upload.remove')} />
          {next && kindOf(next.type) !== file.family && (
            <p className="text-sm text-[var(--color-warning-bold)] dark:text-[var(--color-warning)]">{t('replace.kindDiffers', { next: t(`family.${kindOf(next.type)}`), current: t(`family.${file.family}`) })}</p>
          )}
        </FormBody>
      </Modal>
      {shell.discardDialog}
    </>
  );
}
